// biome-ignore-all lint/suspicious/noImplicitAnyLet: TODO - address later
/**
 * CSV Export Utility
 * Provides functionality to export data to CSV files.
 */

import type { UnifiedTableColumn } from "@/types/table-column";

/**
 * Escapes a string value for CSV format
 */
function escapeCSV(value: string): string {
  // If the value contains quotes, commas, or newlines, it needs special handling
  if (/[",\n\r]/.test(value)) {
    // Double the quotes and wrap in quotes
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Gets nested value from an object
 */
function getNestedValue<T>(obj: T, key: string | number | symbol): unknown {
  if (typeof key !== "string") {
    return (obj as Record<string | number | symbol, unknown>)[key];
  }

  const keys = key.split(".");
  let value: unknown = obj;

  for (const k of keys) {
    if (value === null || value === undefined) return null;
    value = (value as Record<string, unknown>)[k];
  }

  return value;
}

/**
 * Creates a CSV string from data and columns
 */
export function createCSVString<T>(
  data: T[],
  columns: UnifiedTableColumn<T>[],
  delimiter: string = ",",
): string {
  // Generate header row
  const headerRow = columns.map((col) => escapeCSV(col.title)).join(delimiter);

  // Generate data rows
  const dataRows = data.map((item) => {
    return columns
      .map((column) => {
        let value;

        if (column.exportValue) {
          // Use custom getter if provided
          value = column.exportValue(item);
        } else {
          // Get raw value from the object
          value = getNestedValue(item, column.key);

          // Handle special cases
          if (value === null || value === undefined) {
            value = "";
          } else if (typeof value === "object") {
            if (value instanceof Date) {
              value = value.toISOString();
            } else {
              // Just convert objects to JSON string representation
              value = JSON.stringify(value);
            }
          }
        }

        return escapeCSV(String(value));
      })
      .join(delimiter);
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join("\n");
}

/**
 * Exports data to a CSV file
 */
export async function exportToCSV<T>(
  data: T[],
  columns: UnifiedTableColumn<T>[],
  filename: string,
  options?: {
    encoding?: string;
    delimiter?: string;
  },
): Promise<void> {
  const delimiter = options?.delimiter || ",";

  // Filter and convert columns for export
  const exportColumns = columns.filter((column) => column.exportable !== false);

  const csvContent = createCSVString(data, exportColumns, delimiter);

  // Create a Blob containing the CSV data
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  // Create a temporary download link
  const link = document.createElement("a");

  // Create download URL
  const url = URL.createObjectURL(blob);
  link.href = url;

  // Set the filename - ensure it's a string and add .csv extension if needed
  const safeFilename = String(filename || "export");
  link.download = safeFilename.endsWith(".csv")
    ? safeFilename
    : `${safeFilename}.csv`;

  // Append to body, trigger download, and clean up
  document.body.appendChild(link);
  link.click();

  // Clean up
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}
